<script lang="ts">
  import './layout.css';
  import { slide } from 'svelte/transition';
  import { goto } from '$app/navigation';
  import { page } from '$app/state';
  import { resolve } from '$app/paths';
  import { DropdownMenu } from 'bits-ui';
  import { ChevronDown, Menu, Moon, Sun, X } from '@lucide/svelte';
  import { ModeWatcher, toggleMode } from 'mode-watcher';
  import favicon from '$lib/assets/favicon.svg';
  import { Toaster } from '$lib/components/ui/sonner';
  import { auth } from '$lib/auth.svelte';
  import { logout } from '$lib/api/auth';
  import { hasAnyAdminPermission } from '$lib/permissions';
  import type { LayoutProps } from './$types';

  let { children, data }: LayoutProps = $props();

  const user = $derived(
    auth.user
      ? {
          name: `${auth.user.firstName} ${auth.user.lastName}`.trim(),
          initials: initials(auth.user.firstName, auth.user.lastName)
        }
      : { name: '', initials: '' }
  );

  function initials(first: string, last: string): string {
    const f = first.trim().charAt(0).toUpperCase();
    const l = last.trim().charAt(0).toUpperCase();
    return `${f}${l}` || '?';
  }

  async function handleLogout() {
    await logout();
    await goto(resolve('/login'));
  }

  // `data.dojo` is null on the public auth pages — we don't fetch the schema
  // bundle until the user's signed in. Guarded so the derivation doesn't blow
  // up when the layout briefly renders without a dojo.
  //
  // Defensive sort by listOrder: the API now returns sorted order arrays,
  // but if a stale client receives an unsorted programOrder/courseOrder we
  // still want the menu to render consistently between navigations.
  function byListOrder<T extends { id: string; listOrder: number }>(items: (T | undefined)[]): T[] {
    return items
      .filter((x): x is T => x !== undefined)
      .sort((a, b) =>
        a.listOrder === b.listOrder ? a.id.localeCompare(b.id) : a.listOrder - b.listOrder
      );
  }

  const classGroups = $derived(
    data.dojo
      ? byListOrder(data.dojo.programOrder.map((id) => data.dojo!.programs[id]))
          .map((program) => ({
            program,
            courses: byListOrder(
              (data.dojo!.courseOrder[program.id] ?? []).map(
                (courseId) => data.dojo!.courses[program.id]?.[courseId]
              )
            ).filter((c) => !c.archived)
          }))
          .filter((g) => !g.program.archived && g.courses.length > 0)
      : []
  );

  const classesActive = $derived(page.url.pathname.startsWith('/classes'));
  const reportsActive = $derived(page.url.pathname.startsWith('/reports'));
  const isAuthPage = $derived(page.url.pathname.startsWith('/login'));
  // Kiosk pages (/checkin/<program>/<courseId>) suppress the nav entirely so
  // the wall-mounted iPad can't navigate away. The /checkin picker keeps nav.
  const isKioskPage = $derived(
    page.url.pathname.split('/').filter(Boolean).length >= 3 &&
      page.url.pathname.startsWith('/checkin/')
  );

  let mobileOpen = $state(false);

  // Reading page.url.pathname registers a reactive dep so the effect re-runs
  // on navigation; `void` keeps the no-unused-expressions linter happy.
  $effect(() => {
    void page.url.pathname;
    mobileOpen = false;
  });

  const navLink =
    'hover:bg-accent focus:bg-accent block rounded px-2 py-1.5 text-sm outline-none focus-visible:ring-1';
</script>

<svelte:head><link rel="icon" href={favicon} /></svelte:head>
<ModeWatcher />
<Toaster />

{#if isAuthPage}
  <div class="flex min-h-screen items-center justify-center bg-background p-6 text-foreground">
    {@render children()}
  </div>
{:else if isKioskPage}
  <div class="min-h-screen bg-background text-foreground">
    {@render children()}
  </div>
{:else}
  <div class="min-h-screen bg-background text-foreground print:bg-white">
    <header class="sticky top-0 z-40 border-b bg-background print:hidden">
      <div class="container mx-auto flex items-center justify-between px-6 py-3">
        <div class="flex items-center gap-6">
          <a href={resolve('/students')} class="text-base font-semibold tracking-tight">
            {data.dojo?.name ?? ''}
          </a>
          <nav class="hidden items-center gap-1 md:flex">
            <a
              href={resolve('/students')}
              class="rounded px-2.5 py-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground {page.url.pathname.startsWith(
                '/students'
              )
                ? 'font-medium text-foreground'
                : ''}"
            >
              Students
            </a>

            <a
              href={resolve('/enroll')}
              class="rounded px-2.5 py-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground {page.url.pathname.startsWith(
                '/enroll'
              )
                ? 'font-medium text-foreground'
                : ''}"
            >
              Enroll
            </a>

            <a
              href={resolve('/accounts')}
              class="rounded px-2.5 py-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground {page.url.pathname.startsWith(
                '/accounts'
              )
                ? 'font-medium text-foreground'
                : ''}"
            >
              Accounts
            </a>

            <DropdownMenu.Root>
              <DropdownMenu.Trigger
                class="inline-flex items-center gap-1 rounded px-2.5 py-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground {classesActive
                  ? 'font-medium text-foreground'
                  : ''}"
              >
                Classes
                <ChevronDown class="size-3.5 opacity-60" />
              </DropdownMenu.Trigger>
              <DropdownMenu.Content
                class="z-50 min-w-64 rounded-lg border bg-popover p-2 text-popover-foreground shadow-md"
              >
                {#each classGroups as group, i (group.program.id)}
                  {#if i > 0}<div class="my-1.5 h-px bg-border"></div>{/if}
                  <div
                    class="mb-1 px-2 text-xs font-semibold tracking-wide text-muted-foreground uppercase"
                  >
                    {group.program.name}
                  </div>
                  {#each group.courses as course (course.id)}
                    <a href={resolve(`/classes/${group.program.id}/${course.id}`)} class={navLink}>
                      {course.name}
                    </a>
                  {/each}
                {/each}
              </DropdownMenu.Content>
            </DropdownMenu.Root>

            <DropdownMenu.Root>
              <DropdownMenu.Trigger
                class="inline-flex items-center gap-1 rounded px-2.5 py-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground {reportsActive
                  ? 'font-medium text-foreground'
                  : ''}"
              >
                Reports
                <ChevronDown class="size-3.5 opacity-60" />
              </DropdownMenu.Trigger>
              <DropdownMenu.Content
                class="z-50 min-w-48 rounded-lg border bg-popover p-2 text-popover-foreground shadow-md"
              >
                <a href={resolve('/reports/birthday')} class={navLink}>Birthday</a>
                <a href={resolve('/reports/anniversary')} class={navLink}>Anniversary</a>
                <a href={resolve('/reports/best-attendance')} class={navLink}>Best Attendance</a>
                <div class="my-1.5 h-px bg-border"></div>
                <a href={resolve('/reports/attendance')} class={navLink}>Attendance</a>
                <a href={resolve('/reports/absence')} class={navLink}>Absence</a>
                <div class="my-1.5 h-px bg-border"></div>
                <a href={resolve('/reports/last-promotion')} class={navLink}>Last Promotion</a>
                <div class="my-1.5 h-px bg-border"></div>
                <a href={resolve('/reports/student-pin')} class={navLink}>Student PINs</a>
              </DropdownMenu.Content>
            </DropdownMenu.Root>

            <a
              href={resolve('/attendance')}
              class="rounded px-2.5 py-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground {page.url.pathname.startsWith(
                '/attendance'
              )
                ? 'font-medium text-foreground'
                : ''}"
            >
              Attendance Card
            </a>

            <a
              href={resolve('/checkin')}
              class="rounded px-2.5 py-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground {page.url.pathname.startsWith(
                '/checkin'
              )
                ? 'font-medium text-foreground'
                : ''}"
            >
              Check in
            </a>
          </nav>
        </div>

        <div class="flex items-center gap-2">
          <!-- User menu (desktop) -->
          <DropdownMenu.Root>
            <DropdownMenu.Trigger
              class="hidden items-center gap-2 rounded px-2.5 py-1.5 text-sm transition-colors hover:bg-accent md:flex"
            >
              <div
                class="flex size-6 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground"
              >
                {user.initials}
              </div>
              {user.name}
            </DropdownMenu.Trigger>
            <DropdownMenu.Content
              class="z-50 min-w-40 rounded-lg border bg-popover p-2 text-popover-foreground shadow-md"
            >
              <a href={resolve('/profile/edit')} class={navLink}>Edit Profile</a>
              <a href={resolve('/profile/sessions')} class={navLink}>Active sessions</a>
              {#if hasAnyAdminPermission()}
                <a href={resolve('/admin')} class={navLink}>Admin</a>
              {/if}
              <button onclick={toggleMode} class="flex w-full items-center gap-2 {navLink}">
                <Sun class="size-3.5 dark:hidden" /><Moon class="hidden size-3.5 dark:block" />
                <span class="dark:hidden">Dark mode</span><span class="hidden dark:block"
                  >Light mode</span
                >
              </button>
              <div class="my-1.5 h-px bg-border"></div>
              <button
                onclick={handleLogout}
                class="w-full text-left text-muted-foreground {navLink}"
              >
                Log out
              </button>
            </DropdownMenu.Content>
          </DropdownMenu.Root>

          <button
            onclick={() => (mobileOpen = !mobileOpen)}
            class="-mr-1 inline-flex size-9 items-center justify-center rounded-md hover:bg-accent focus-visible:ring-1 focus-visible:ring-ring focus-visible:outline-none md:hidden"
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
          >
            {#if mobileOpen}
              <X class="size-5" />
            {:else}
              <Menu class="size-5" />
            {/if}
          </button>
        </div>
      </div>

      {#if mobileOpen}
        <div
          transition:slide={{ duration: 150 }}
          class="max-h-[calc(100dvh-3.5rem)] overflow-y-auto overscroll-contain border-t md:hidden"
        >
          <nav class="container mx-auto space-y-1 px-4 py-3">
            <a
              href={resolve('/students')}
              class="block rounded-md px-3 py-2 text-sm font-medium {page.url.pathname.startsWith(
                '/students'
              )
                ? 'bg-accent'
                : 'hover:bg-accent'}"
            >
              Students
            </a>

            <a
              href={resolve('/enroll')}
              class="block rounded-md px-3 py-2 text-sm font-medium {page.url.pathname.startsWith(
                '/enroll'
              )
                ? 'bg-accent'
                : 'hover:bg-accent'}"
            >
              Enroll
            </a>

            <a
              href={resolve('/accounts')}
              class="block rounded-md px-3 py-2 text-sm font-medium {page.url.pathname.startsWith(
                '/accounts'
              )
                ? 'bg-accent'
                : 'hover:bg-accent'}"
            >
              Accounts
            </a>

            <details class="group/classes" open={classesActive}>
              <summary
                class="flex cursor-pointer list-none items-center justify-between rounded-md px-3 py-2 text-sm font-medium hover:bg-accent [&::-webkit-details-marker]:hidden {classesActive
                  ? 'bg-accent'
                  : ''}"
              >
                <span>Classes</span>
                <ChevronDown
                  class="size-4 opacity-60 transition-transform group-open/classes:rotate-180"
                />
              </summary>
              <div class="mt-1 ml-2 space-y-1 border-l pl-2">
                {#each classGroups as group (group.program.id)}
                  <details class="group/program">
                    <summary
                      class="flex cursor-pointer list-none items-center justify-between rounded-md px-3 py-2 text-sm text-muted-foreground hover:bg-accent [&::-webkit-details-marker]:hidden"
                    >
                      <span>{group.program.name}</span>
                      <ChevronDown
                        class="size-3.5 opacity-60 transition-transform group-open/program:rotate-180"
                      />
                    </summary>
                    <div class="ml-2 space-y-1 border-l pl-2">
                      {#each group.courses as course (course.id)}
                        {@const href = resolve(`/classes/${group.program.id}/${course.id}`)}
                        <a
                          {href}
                          class="block rounded-md px-3 py-2 text-sm {page.url.pathname === href
                            ? 'bg-accent font-medium'
                            : 'hover:bg-accent'}"
                        >
                          {course.name}
                        </a>
                      {/each}
                    </div>
                  </details>
                {/each}
              </div>
            </details>

            <details class="group/reports" open={reportsActive}>
              <summary
                class="flex cursor-pointer list-none items-center justify-between rounded-md px-3 py-2 text-sm font-medium hover:bg-accent {reportsActive
                  ? 'bg-accent'
                  : ''}"
              >
                <span>Reports</span>
                <ChevronDown
                  class="size-4 opacity-60 transition-transform group-open/reports:rotate-180"
                />
              </summary>
              <div class="mt-1 ml-2 space-y-1 border-l pl-2">
                {#each [{ href: resolve('/reports/birthday'), label: 'Birthday' }, { href: resolve('/reports/anniversary'), label: 'Anniversary' }, { href: resolve('/reports/best-attendance'), label: 'Best Attendance' }, { href: resolve('/reports/attendance'), label: 'Attendance' }, { href: resolve('/reports/absence'), label: 'Absence' }, { href: resolve('/reports/last-promotion'), label: 'Last Promotion' }, { href: resolve('/reports/student-pin'), label: 'Student PINs' }] as report (report.href)}
                  <a
                    href={report.href}
                    class="block rounded-md px-3 py-2 text-sm {page.url.pathname === report.href
                      ? 'bg-accent font-medium'
                      : 'hover:bg-accent'}"
                  >
                    {report.label}
                  </a>
                {/each}
              </div>
            </details>

            <a
              href={resolve('/attendance')}
              class="mt-2 block rounded-md px-3 py-2 text-sm font-medium {page.url.pathname.startsWith(
                '/attendance'
              )
                ? 'bg-accent'
                : 'hover:bg-accent'}"
            >
              Attendance Card
            </a>

            <a
              href={resolve('/checkin')}
              class="block rounded-md px-3 py-2 text-sm font-medium {page.url.pathname.startsWith(
                '/checkin'
              )
                ? 'bg-accent'
                : 'hover:bg-accent'}"
            >
              Check in
            </a>

            <div class="mt-2 border-t pt-3">
              <div class="flex items-center gap-3 px-3 py-2">
                <div
                  class="flex size-8 items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground"
                >
                  {user.initials}
                </div>
                <span class="text-sm font-medium">{user.name}</span>
              </div>
              <a
                href={resolve('/profile/edit')}
                class="block rounded-md px-3 py-2 text-sm hover:bg-accent {page.url.pathname ===
                '/profile/edit'
                  ? 'bg-accent font-medium'
                  : ''}"
              >
                Edit Profile
              </a>
              <a
                href={resolve('/profile/sessions')}
                class="block rounded-md px-3 py-2 text-sm hover:bg-accent {page.url.pathname ===
                '/profile/sessions'
                  ? 'bg-accent font-medium'
                  : ''}"
              >
                Active sessions
              </a>
              {#if hasAnyAdminPermission()}
                <a
                  href={resolve('/admin')}
                  class="block rounded-md px-3 py-2 text-sm hover:bg-accent {page.url.pathname.startsWith(
                    '/admin'
                  )
                    ? 'bg-accent font-medium'
                    : ''}"
                >
                  Admin
                </a>
              {/if}
              <button
                onclick={toggleMode}
                class="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm hover:bg-accent"
              >
                <Sun class="size-4 dark:hidden" /><Moon class="hidden size-4 dark:block" />
                <span class="dark:hidden">Dark mode</span><span class="hidden dark:block"
                  >Light mode</span
                >
              </button>
              <button
                onclick={handleLogout}
                class="block w-full rounded-md px-3 py-2 text-left text-sm text-muted-foreground hover:bg-accent"
              >
                Log out
              </button>
            </div>
          </nav>
        </div>
      {/if}
    </header>

    {@render children()}
  </div>
{/if}
